import React from 'react';
import './icons.css';

/**
 * PetsFostering animated icons — original artwork, motion driven by `icons.css`.
 *
 * All loaders share a 64×48 stage so a four-legged animal has room to move, and
 * all of them inherit `currentColor`, so a loader dropped inside any themed
 * surface picks up that surface's text colour.
 *
 * Motion is defined in CSS (not SMIL) for one reason: `prefers-reduced-motion`.
 * The media query at the bottom of `icons.css` freezes every loader into a
 * legible still frame with a slow opacity breath.
 */

const LOADER_RATIO = 48 / 64;

const LoaderSvg = ({
  size = 64,
  strokeWidth = 2.2,
  title,
  className = '',
  animationClass = '',
  children,
  ...rest
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={Math.round(size * LOADER_RATIO)}
    viewBox="0 0 64 48"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`pf-anim ${animationClass} ${className}`.trim()}
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : 'true'}
    aria-live={title ? 'polite' : undefined}
    focusable="false"
    {...rest}
  >
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
);

/**
 * A single paw print drawn around the origin, ~7.4 × 8.6 units.
 * Three nested groups on purpose: the outer one places it, the middle one is
 * what CSS animates (so an animated `transform` never clobbers positioning),
 * the inner one scales it.
 */
const TrailPaw = ({ x, y, rotate = 0, scale = 1 }) => (
  <g transform={`translate(${x} ${y}) rotate(${rotate})`}>
    <g>
      <g transform={`scale(${scale})`} fill="currentColor" stroke="none">
        <ellipse cx="-2.75" cy="-2.3" rx="0.95" ry="1.3" transform="rotate(-22 -2.75 -2.3)" />
        <ellipse cx="-0.95" cy="-3.3" rx="0.95" ry="1.4" />
        <ellipse cx="0.95" cy="-3.3" rx="0.95" ry="1.4" />
        <ellipse cx="2.75" cy="-2.3" rx="0.95" ry="1.3" transform="rotate(22 2.75 -2.3)" />
        <ellipse cx="0" cy="1.2" rx="3.1" ry="2.7" />
      </g>
    </g>
  </g>
);

/* ------------------------------------------------------------------ *
 * Primary loaders
 * ------------------------------------------------------------------ */

/**
 * Paw prints walking across the stage, one after another.
 * The house default for full-page and section loading.
 */
export const PawTrailLoader = (props) => (
  <LoaderSvg animationClass="pf-anim-paw-trail" {...props}>
    <TrailPaw x={9} y={32} rotate={-14} />
    <TrailPaw x={20.5} y={19} rotate={10} />
    <TrailPaw x={32} y={32} rotate={-14} />
    <TrailPaw x={43.5} y={19} rotate={10} />
    <TrailPaw x={55} y={32} rotate={-14} />
  </LoaderSvg>
);

/** Circular arc spinner with a breathing paw at its centre. The inline default. */
export const PawSpinner = (props) => (
  <LoaderSvg strokeWidth={3} {...props}>
    <circle cx="32" cy="24" r="15" opacity="0.16" />
    <path className="pf-anim-arc" d="M32 9a15 15 0 0 1 15 15" />
    <g className="pf-anim-core" fill="currentColor" stroke="none">
      <ellipse cx="28.9" cy="21.6" rx="1.05" ry="1.35" transform="rotate(-22 28.9 21.6)" />
      <ellipse cx="30.9" cy="20.4" rx="1.05" ry="1.45" />
      <ellipse cx="33.1" cy="20.4" rx="1.05" ry="1.45" />
      <ellipse cx="35.1" cy="21.6" rx="1.05" ry="1.35" transform="rotate(22 35.1 21.6)" />
      <ellipse cx="32" cy="25.4" rx="3.1" ry="2.6" />
    </g>
  </LoaderSvg>
);

/* ------------------------------------------------------------------ *
 * Characters
 * ------------------------------------------------------------------ */

/** Dog running to the right over a scrolling ground line. */
export const RunningDogLoader = (props) => (
  <LoaderSvg {...props}>
    <g className="pf-anim-runner">
      <path
        className="pf-anim-tail-dog"
        style={{ transformOrigin: '21px 19px' }}
        d="M21 19c-4.6-1.4-6.6-4.8-5.2-8.2"
      />
      <rect x="20" y="17" width="24" height="11" rx="5.5" />
      <path d="M41.5 18.4 46.4 13.6" />
      <path d="M47.4 9.2c-1.6-2.4-.6-4.6 2-4.8" />
      <circle cx="51" cy="13.5" r="5.6" />
      <path d="M55.6 11.4h3.2a2.5 2.5 0 0 1 0 5h-3.2" />
      <path className="pf-anim-leg" style={{ transformOrigin: '26px 27px' }} d="M26 27v10.5" />
      <path
        className="pf-anim-leg pf-anim-leg--phase-b"
        style={{ transformOrigin: '30.5px 27px' }}
        d="M30.5 27v10.5"
        opacity="0.5"
      />
      <path
        className="pf-anim-leg pf-anim-leg--phase-b"
        style={{ transformOrigin: '38.5px 27px' }}
        d="M38.5 27v10.5"
      />
      <path
        className="pf-anim-leg"
        style={{ transformOrigin: '43px 27px' }}
        d="M43 27v10.5"
        opacity="0.5"
      />
      <g fill="currentColor" stroke="none">
        <circle cx="52.4" cy="12" r="1" />
        <circle cx="59.4" cy="13.9" r="1.1" />
      </g>
    </g>
    <path className="pf-anim-ground" d="M6 40.5h52" opacity="0.28" />
  </LoaderSvg>
);

/** Cat running to the right, lighter frame and a longer waving tail. */
export const RunningCatLoader = (props) => (
  <LoaderSvg {...props}>
    <g className="pf-anim-runner">
      <path
        className="pf-anim-tail-cat"
        style={{ transformOrigin: '22px 21px' }}
        d="M22 21c-6.4.6-8.8-3.4-6.4-7.4"
      />
      <rect x="22" y="19" width="22" height="9" rx="4.5" />
      <path d="M41.5 20.4 45.6 16.6" />
      <path d="M46.6 13.4 45.6 9.2l4 2.2" />
      <path d="M53.4 13.4 54.4 9.2l-4 2.2" />
      <circle cx="50" cy="17" r="5.2" />
      <path d="M55.4 17h3.4" opacity="0.5" />
      <path d="M55.2 19l3.2.9" opacity="0.5" />
      <path className="pf-anim-leg" style={{ transformOrigin: '26.5px 27.5px' }} d="M26.5 27.5v10" />
      <path
        className="pf-anim-leg pf-anim-leg--phase-b"
        style={{ transformOrigin: '30.5px 27.5px' }}
        d="M30.5 27.5v10"
        opacity="0.5"
      />
      <path
        className="pf-anim-leg pf-anim-leg--phase-b"
        style={{ transformOrigin: '38px 27.5px' }}
        d="M38 27.5v10"
      />
      <path
        className="pf-anim-leg"
        style={{ transformOrigin: '42px 27.5px' }}
        d="M42 27.5v10"
        opacity="0.5"
      />
      <g fill="currentColor" stroke="none">
        <circle cx="51.6" cy="16" r="0.9" />
        <circle cx="54.6" cy="18" r="0.9" />
      </g>
    </g>
    <path className="pf-anim-ground" d="M6 40.5h52" opacity="0.28" />
  </LoaderSvg>
);

/** Happy dog seen from behind, tail wagging. Success and "we're on it" moments. */
export const TailWagLoader = (props) => (
  <LoaderSvg {...props}>
    <path
      className="pf-anim-wag"
      style={{ transformOrigin: '40px 25px' }}
      d="M40 25c5-1.4 7.2-5 6-9"
    />
    <g className="pf-anim-wag-body">
      <ellipse cx="30" cy="29" rx="11.5" ry="8.5" />
      <path d="M22.6 21.6c-1.2-3 .2-5.2 2.8-5.4" />
      <path d="M37.4 21.6c1.2-3-.2-5.2-2.8-5.4" />
      <path d="M24.4 37.2v3.2" />
      <path d="M35.6 37.2v3.2" />
    </g>
    <path d="M6 43h52" opacity="0.28" strokeDasharray="6 7" />
  </LoaderSvg>
);

/* ------------------------------------------------------------------ *
 * Task-flavoured loaders
 * ------------------------------------------------------------------ */

/** Beating heart over a running ECG trace. Vet records, health, saving a profile. */
export const HeartbeatLoader = (props) => (
  <LoaderSvg {...props}>
    <path
      className="pf-anim-ecg"
      d="M4 39h8l3-6 4 12 3.5-9h6.5l3-5 3.5 9 2.5-6H60"
      opacity="0.45"
    />
    <path
      className="pf-anim-heart"
      d="M32 31.6 24.4 24a5.37 5.37 0 0 1 7.6-7.6 5.37 5.37 0 0 1 7.6 7.6Z"
    />
  </LoaderSvg>
);

/** Kibble dropping into a bowl. Uploads, imports, syncing, seeding data. */
export const BowlFillLoader = (props) => (
  <LoaderSvg {...props}>
    <g>
      <circle className="pf-anim-kibble" cx="25.5" cy="28" r="2.2" />
      <circle className="pf-anim-kibble" cx="30.5" cy="26.6" r="2.2" />
      <circle className="pf-anim-kibble" cx="35.5" cy="28.2" r="2.2" />
      <circle className="pf-anim-kibble" cx="40" cy="27.4" r="2.2" />
    </g>
    <ellipse cx="32" cy="30" rx="18" ry="4" />
    <path d="M14 30c0 7 8 12.5 18 12.5S50 37 50 30" />
  </LoaderSvg>
);

/** Bone tumbling end over end. Playful inline loader for short waits. */
export const BoneSpinnerLoader = (props) => (
  <LoaderSvg {...props}>
    <g className="pf-anim-bone" fill="currentColor" stroke="none">
      <rect x="25" y="22" width="14" height="4" />
      <circle cx="25" cy="21.5" r="3.2" />
      <circle cx="25" cy="26.5" r="3.2" />
      <circle cx="39" cy="21.5" r="3.2" />
      <circle cx="39" cy="26.5" r="3.2" />
    </g>
  </LoaderSvg>
);

/** Paw-stamped play ball bouncing with a squash on impact. Light, short waits. */
export const BouncingBallLoader = (props) => (
  <LoaderSvg {...props}>
    <ellipse
      className="pf-anim-ball-shadow"
      cx="32"
      cy="43"
      rx="9"
      ry="1.8"
      fill="currentColor"
      stroke="none"
    />
    <g className="pf-anim-ball">
      <g className="pf-anim-ball-inner">
        <circle cx="32" cy="33" r="7.5" />
        <g fill="currentColor" stroke="none">
          <ellipse cx="29.9" cy="31.2" rx="0.75" ry="1" transform="rotate(-22 29.9 31.2)" />
          <ellipse cx="31.3" cy="30.4" rx="0.75" ry="1.05" />
          <ellipse cx="32.7" cy="30.4" rx="0.75" ry="1.05" />
          <ellipse cx="34.1" cy="31.2" rx="0.75" ry="1" transform="rotate(22 34.1 31.2)" />
          <ellipse cx="32" cy="34.1" rx="2.4" ry="2" />
        </g>
      </g>
    </g>
  </LoaderSvg>
);

/** Three paws bobbing in sequence. The "is typing…" indicator in Messages. */
export const TypingPawsLoader = (props) => (
  <LoaderSvg animationClass="pf-anim-typing" {...props}>
    <TrailPaw x={20} y={26} scale={0.85} />
    <TrailPaw x={32} y={26} scale={0.85} />
    <TrailPaw x={44} y={26} scale={0.85} />
  </LoaderSvg>
);

export const ANIMATED_ICONS = {
  'loader-paw-trail': PawTrailLoader,
  'loader-paw-spinner': PawSpinner,
  'loader-running-dog': RunningDogLoader,
  'loader-running-cat': RunningCatLoader,
  'loader-tail-wag': TailWagLoader,
  'loader-heartbeat': HeartbeatLoader,
  'loader-bowl-fill': BowlFillLoader,
  'loader-bone-spinner': BoneSpinnerLoader,
  'loader-bouncing-ball': BouncingBallLoader,
  'loader-typing-paws': TypingPawsLoader,
};

export const ANIMATED_ICON_IDS = Object.keys(ANIMATED_ICONS);

export default ANIMATED_ICONS;
